// KPIs do Relatório de Ligações, derivados do resumo do período
// (GET /v1/calls/summary). Nada aqui vai ao banco: o back já devolve os
// agregados e esta camada só os arruma para a faixa de cards.
import type { CallStatus, CallSummary } from "./api.js";

export interface CallKpi {
  count: number;
  pct: number;
}

export interface CallKpis {
  total: number;
  answered: CallKpi;
  invalid: CallKpi;
  noAnswer: CallKpi;
  failed: CallKpi;
  // Falhas que NÃO são número inválido (operadora, rede, ocupado…). O `failed`
  // inclui os inválidos — `byFailureReason` só agrupa FAILED —, então somar os
  // dois contaria o mesmo número duas vezes.
  technicalFailed: CallKpi;
  // Atendidas pelo canal sem pessoa do outro lado (caixa postal/URA): o
  // atendimento técnico menos o contato humano, por operador e nunca negativo.
  notHuman: CallKpi;
  // Operadores com ao menos uma ligação no período; a linha sem responsável
  // (userId null) não é gente.
  activeOperators: number;
  // Segundos falados no período e a média por ligação atendida. A média é
  // null quando não houve atendimento — melhor um traço do que um zero que
  // parece medição.
  totalTalkSec: number;
  avgTalkSec: number | null;
}

// Percentual sobre o total do período, uma casa decimal. Total zero devolve 0
// em vez de NaN.
function pctOf(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
}

function countByStatus(summary: CallSummary, status: CallStatus): number {
  return summary.byStatus.find((s) => s.status === status)?.count ?? 0;
}

// Motivos que significam "esse número não presta": o worker devolve
// `invalid_number` quando a operadora recusa a numeração (Q.850 1/3/22/28) e
// `no_target` quando o lead sequer tinha telefone discável. Para quem opera a
// base os dois pedem a mesma ação — revisar o cadastro —, então contam junto.
const INVALID_REASONS = ["invalid_number", "no_target"];

export function buildCallKpis(summary: CallSummary): CallKpis {
  const total = summary.totalCalls;
  // `answered` é o atendimento técnico do canal e inclui caixa postal/URA.
  // `humanContacts` usa a tabulação revisada para contar só quando houve pessoa.
  // O fallback mantém o front compatível durante uma atualização gradual do back.
  const answered = summary.byAgent.reduce(
    (acc, agent) => acc + (agent.humanContacts ?? agent.answered),
    0,
  );
  const invalid = summary.byFailureReason
    .filter((f) => f.reason !== null && INVALID_REASONS.includes(f.reason))
    .reduce((acc, f) => acc + f.count, 0);
  const notHuman = summary.byAgent.reduce(
    (acc, agent) => acc + Math.max(0, agent.answered - (agent.humanContacts ?? agent.answered)),
    0,
  );
  const failed = countByStatus(summary, "FAILED");
  const technicalFailed = Math.max(0, failed - invalid);
  const totalTalkSec = summary.byAgent.reduce(
    (acc, agent) => acc + (agent.humanTalkSec ?? agent.totalDurationSec),
    0,
  );

  return {
    total,
    answered: { count: answered, pct: pctOf(answered, total) },
    invalid: { count: invalid, pct: pctOf(invalid, total) },
    noAnswer: {
      count: countByStatus(summary, "NO_ANSWER"),
      pct: pctOf(countByStatus(summary, "NO_ANSWER"), total),
    },
    failed: { count: failed, pct: pctOf(failed, total) },
    technicalFailed: { count: technicalFailed, pct: pctOf(technicalFailed, total) },
    notHuman: { count: notHuman, pct: pctOf(notHuman, total) },
    activeOperators: summary.byAgent.filter((agent) => agent.userId && agent.total > 0).length,
    totalTalkSec,
    avgTalkSec: answered > 0 ? Math.round(totalTalkSec / answered) : null,
  };
}
